import { batch } from 'react-redux';
import { toast } from 'react-toastify';

import { useAppDispatch } from 'redux/store/store.types';
import { getApiErrorMessage } from 'utils';

import {
  useAddCategoryMutation,
  useAddCategoryQuestionMutation,
  useDeleteCategoryMutation,
  useDeleteCategoryQuestionMutation,
  useEditCategoryMutation,
  useLazyGetHelpCategoriesQuery,
  useLazyGetHelpCategoryQuery,
  useLazyGetHelpLanguagesQuery,
  useUpdateCategoriesOrderMutation,
  useUpdateCategoryQuestionMutation,
} from '../api/market-help.api';
import {
  clearState,
  setCurrentModal,
  setHelpCategories,
  setHelpLanguages,
  setIsLoading,
  setSelectedCategory,
  setSelectedLanguage,
  setSelectedQuestion,
} from '../store/market-help.slice';
import {
  AddCategoryPayload,
  AddCategoryQuestionPayload,
  DeleteCategoryPayload,
  DeleteCategoryQuestionPayload,
  EditCategoryPayload,
  GetCategoriesPayload,
  GetCategoryPayload,
  IHelpCategory,
  IHelpQuestion,
  IMarketHelpModal,
  UpdateCategoriesOrderPayload,
  UpdateCategoryQuestionPayload,
} from '../types/market-help.type';

interface OpenModal {
  modal: IMarketHelpModal;
  helpCategory?: IHelpCategory;
  helpQuestion?: IHelpQuestion;
}

interface UseMarketHelp {
  getHelpLanguagesHandler(): Promise<boolean>;
  getHelpCategoriesHandler(payload: GetCategoriesPayload): Promise<boolean>;
  getHelpCategoryHandler(payload: GetCategoryPayload): Promise<boolean>;
  addHelpCategoryHandler(payload: AddCategoryPayload): Promise<IHelpCategory | null>;
  updateHelpCategoryHandler(payload: EditCategoryPayload): Promise<IHelpCategory | null>;
  deleteHelpCategoryHandler(payload: DeleteCategoryPayload): Promise<boolean>;
  updateCategoriesOrderHandler(payload: UpdateCategoriesOrderPayload): Promise<boolean>;
  addHelpCategoryQuestionHandler(payload: AddCategoryQuestionPayload): Promise<IHelpCategory | null>;
  updateHelpCategoryQuestionHandler(payload: UpdateCategoryQuestionPayload): Promise<IHelpCategory | null>;
  deleteHelpCategoryQuestionHandler(payload: DeleteCategoryQuestionPayload): Promise<boolean>;
  openMarketHelpModal(payload: OpenModal): void;
  closeMarketHelpModal(): void;
  clearMarketHelpState(): void;
}

export const useMarketHelp = (): UseMarketHelp => {
  const dispatch = useAppDispatch();

  const [getHelpLanguagesQuery] = useLazyGetHelpLanguagesQuery();
  const [getHelpCategoriesQuery] = useLazyGetHelpCategoriesQuery();
  const [getHelpCategoryQuery] = useLazyGetHelpCategoryQuery();
  const [addCategoryMutation] = useAddCategoryMutation();
  const [editCategoryMutation] = useEditCategoryMutation();
  const [deleteCategoryMutation] = useDeleteCategoryMutation();
  const [updateCategoriesOrderQuery] = useUpdateCategoriesOrderMutation();
  const [addCategoryQuestionMutation] = useAddCategoryQuestionMutation();
  const [updateCategoryQuestionMutation] = useUpdateCategoryQuestionMutation();
  const [deleteCategoryQuestionMutation] = useDeleteCategoryQuestionMutation();

  const addHelpCategoryHandler = async (payload: AddCategoryPayload) => {
    const { data: response, error } = await addCategoryMutation(payload);
    if (error || !response?.data) {
      toast.error(getApiErrorMessage(error) || 'Failed to add help category!');
      return null;
    }
    return response.data;
  };

  const updateHelpCategoryHandler = async (payload: EditCategoryPayload) => {
    const { data: response, error } = await editCategoryMutation(payload);
    if (error || !response?.data) {
      toast.error(getApiErrorMessage(error) || 'Failed to edit help category!');
      return null;
    }
    return response.data;
  };

  const getHelpCategoryHandler = async (payload: GetCategoryPayload) => {
    dispatch(setIsLoading(true));
    const { data: response, error } = await getHelpCategoryQuery(payload);
    dispatch(setIsLoading(false));
    if (error || !response?.data) {
      toast.error(getApiErrorMessage(error) || 'Failed to get help category!');
      return false;
    }
    dispatch(setSelectedCategory(response.data));
    return true;
  };

  const getHelpLanguagesHandler = async () => {
    dispatch(setIsLoading(true));
    const { data: response, error } = await getHelpLanguagesQuery();
    dispatch(setIsLoading(false));
    if (error || !response?.data) {
      toast.error(getApiErrorMessage(error) || 'Failed to get help languages!');
      return false;
    }
    batch(() => {
      dispatch(setHelpLanguages(response.data));
      if (response.data.length) {
        dispatch(setSelectedLanguage(response.data[0]));
      }
    });

    return true;
  };

  const getHelpCategoriesHandler = async (payload: GetCategoriesPayload) => {
    dispatch(setIsLoading(true));
    const { data: response, error } = await getHelpCategoriesQuery(payload);
    dispatch(setIsLoading(false));
    if (error || !response?.data) {
      toast.error(getApiErrorMessage(error) || 'Failed to fetch help categories!');
      return false;
    }
    dispatch(setHelpCategories(response.data));
    return true;
  };

  const deleteHelpCategoryHandler = async (payload: DeleteCategoryPayload) => {
    dispatch(setIsLoading(true));
    const { error } = await deleteCategoryMutation(payload);
    dispatch(setIsLoading(false));
    if (error) {
      toast.error(getApiErrorMessage(error) || 'Failed to delete help category!');
      return false;
    }
    return true;
  };

  const openMarketHelpModal = (payload: OpenModal) => {
    batch(() => {
      dispatch(setCurrentModal(payload.modal));
      if (payload.helpCategory) {
        dispatch(setSelectedCategory(payload.helpCategory));
      }
      if (payload.helpQuestion) {
        dispatch(setSelectedQuestion(payload.helpQuestion));
      }
    });
  };

  const updateCategoriesOrderHandler = async (payload: UpdateCategoriesOrderPayload) => {
    const { error } = await updateCategoriesOrderQuery(payload);
    if (error) {
      toast.error(getApiErrorMessage(error) || 'Failed to save categories position!');
      return false;
    }
    toast.success('Positions saved successfully');
    return true;
  };

  const addHelpCategoryQuestionHandler = async (payload: AddCategoryQuestionPayload) => {
    dispatch(setIsLoading(true));
    const { data: response, error } = await addCategoryQuestionMutation(payload);
    dispatch(setIsLoading(false));
    if (error || !response?.data) {
      toast.error(getApiErrorMessage(error) || 'Failed to add category question!');
      return null;
    }
    toast.success('Category question added!');
    dispatch(setSelectedCategory(response.data));
    return response.data;
  };

  const updateHelpCategoryQuestionHandler = async (payload: UpdateCategoryQuestionPayload) => {
    dispatch(setIsLoading(true));
    const { data: response, error } = await updateCategoryQuestionMutation(payload);
    dispatch(setIsLoading(false));
    if (error || !response?.data) {
      toast.error(getApiErrorMessage(error) || 'Failed to update category question!');
      return null;
    }
    toast.success('Category question updated!');
    dispatch(setSelectedCategory(response.data));
    return response.data;
  };

  const deleteHelpCategoryQuestionHandler = async (payload: DeleteCategoryQuestionPayload) => {
    dispatch(setIsLoading(true));
    const { error } = await deleteCategoryQuestionMutation(payload);
    dispatch(setIsLoading(false));
    if (error) {
      toast.error(getApiErrorMessage(error) || 'Failed to delete category question!');
      return false;
    }
    toast.success('Category question deleted!');
    return true;
  };

  const closeMarketHelpModal = () => {
    batch(() => {
      dispatch(setCurrentModal(null));
      dispatch(setSelectedCategory(null));
      dispatch(setSelectedQuestion(null));
    });
  };

  const clearMarketHelpState = () => {
    dispatch(clearState());
  };

  return {
    getHelpLanguagesHandler,
    clearMarketHelpState,
    getHelpCategoriesHandler,
    getHelpCategoryHandler,
    openMarketHelpModal,
    closeMarketHelpModal,
    addHelpCategoryHandler,
    updateHelpCategoryHandler,
    deleteHelpCategoryHandler,
    updateCategoriesOrderHandler,
    addHelpCategoryQuestionHandler,
    updateHelpCategoryQuestionHandler,
    deleteHelpCategoryQuestionHandler,
  };
};
